import { LivingSculpt } from '../components/LivingSculpt.js';

/**
 * Verification harness for the spatial2 renderer (like /ldi-test before it —
 * read-only, no auth, harmless to leave deployed). Query params drive everything
 * so Playwright + local pipelines can exercise arbitrary gens without code
 * changes:
 *
 *   /spatial2-test?gen=gen_xxx&idx=0&aspect=0.6667            — a live gen
 *   /spatial2-test?gen=gen_xxx&cover=<url>                    — explicit cover
 *   /spatial2-test?src=http://localhost:8000/out/gen_x-0      — LOCAL artifacts:
 *        `src` is the spatial2 asset-dir base (meta.json/fg.png/bg.png/…);
 *        the cover defaults to `<src>/cover.png`.
 *   &relief=1     — Phase-2 displacement (read by the component itself)
 *   &ambient=0    — idle/pointer drive instead of the auto-orbit
 *   &still=1      — pin the EXACT rest pose (sets window.__sculptStill; the
 *                   deterministic frame for the rest-identity numeric gate)
 *   &light=0      — pin key light + content relight at neutral while the
 *                   camera still orbits (sets window.__sculptNeutralLight;
 *                   the ghost/void gates diff orbit frames without the
 *                   intended relight field reading as a ghost)
 *
 * The reverse decoupling (rest camera + swung light) has no query param: the
 * harness sets `window.__sculptLightPose = { lx, ly }` at runtime (read
 * per-frame by applyPose) — the rest_identity relight-variant pass uses it to
 * measure the production relight field in isolation.
 *
 * A gen with no (or failed) spatial2 set MUST render as the premium flat framed
 * print — that fail-closed path is part of what this harness verifies.
 */
export function Spatial2Test() {
  const params = new URLSearchParams(window.location.search);
  const base = params.get('src');
  const gen = params.get('gen');
  const idx = params.get('idx') ?? '0';
  const aspect = parseFloat(params.get('aspect') ?? '1');
  const suffix = params.get('suffix') ?? 'openai-edit';
  const ambient = params.get('ambient') !== '0';
  if (params.get('still') === '1') {
    (window as unknown as { __sculptStill?: boolean }).__sculptStill = true;
  }
  if (params.get('light') === '0') {
    (window as unknown as { __sculptNeutralLight?: boolean }).__sculptNeutralLight = true;
  }

  const spatialUrl = base ?? (gen ? `https://api.gilding.app/images/spatial2/${gen}-${idx}` : null);
  const cover =
    params.get('cover') ??
    (base ? `${base}/cover.png` : gen ? `https://api.gilding.app/images/gen/${gen}/${suffix}-${idx}.png` : null);

  if (!spatialUrl || !cover) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0a0810', color: '#c9c2d6', fontFamily: 'monospace', padding: '2rem' }}>
        <pre>
          {'spatial2-test usage:\n'}
          {'  ?gen=<genId>[&idx=0][&aspect=1][&suffix=openai-edit][&cover=<url>]\n'}
          {'  ?src=<spatial2 asset dir base>[&cover=<url>][&aspect=1]\n'}
          {'  [&relief=1] [&ambient=0]'}
        </pre>
      </div>
    );
  }
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0a0810' }}>
      <LivingSculpt
        src={cover}
        spatialUrl={spatialUrl}
        aspect={aspect}
        ambient={ambient}
        motionHint={false}
        heightCap="86svh"
        alt=""
      />
    </div>
  );
}
